// SPDX-License-Identifier: MIT
// Parse a conversational-turn output (desktop bridge). Mirrors the converse edge
// fn's normalizeOutput (supabase/functions/converse/lib.ts) so the desktop reply
// + model-decided fiche-version bump behave identically to the web path. Takes
// the already-unwrapped inner text (Claude envelope OR sovereign OpenAI body).
import type { MagnifyResult } from '../auth/magnify';
import { extractJsonObject, ClaudeBridgeError } from './claude-output';

export interface ConverseParsed {
  reply: string;
  manifest_changed: boolean;
  manifest?: MagnifyResult;
}

export function parseConverseOutput(inner: string): ConverseParsed {
  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(extractJsonObject(inner)) as Record<string, unknown>;
  } catch (err) {
    throw err instanceof ClaudeBridgeError ? err : new ClaudeBridgeError('converse JSON malformed');
  }
  if (typeof parsed.reply !== 'string' || parsed.reply.trim().length === 0) {
    throw new ClaudeBridgeError('converse output missing reply');
  }
  const changed = parsed.manifest_changed === true;
  if (!changed) return { reply: parsed.reply, manifest_changed: false };

  // Model flagged a change → the manifest must be a complete, usable fiche,
  // else we coerce to a no-change turn (reply still shows, fiche untouched).
  const m = parsed.manifest as Record<string, unknown> | undefined;
  if (
    !m ||
    typeof m.title !== 'string' ||
    !Array.isArray(m.outline) || m.outline.length === 0 ||
    !Array.isArray(m.first_steps) || m.first_steps.length === 0
  ) {
    return { reply: parsed.reply, manifest_changed: false };
  }
  return {
    reply: parsed.reply,
    manifest_changed: true,
    manifest: {
      title: m.title,
      outline: (m.outline as unknown[]).map(String),
      first_steps: (m.first_steps as unknown[]).map(String),
      ...(typeof m.stack === 'string' ? { stack: m.stack } : {}),
    },
  };
}
